/**
 * Storage utilities for localStorage operations with TTL support
 */

/**
 * Set item in localStorage with optional TTL
 *
 * @param {string} key - The storage key
 * @param {any} value - The value to store
 * @param {number} ttl - Time to live in milliseconds (optional)
 */
export function setItem(key, value, ttl = null) {
  if (typeof window === 'undefined' || !window.localStorage) {
    return;
  }

  try {
    const item = {
      value,
      timestamp: Date.now(),
      ttl
    };

    window.localStorage.setItem(key, JSON.stringify(item));
  } catch (error) {
    console.error('Error setting localStorage item:', error);
  }
}

/**
 * Get item from localStorage with TTL check
 *
 * @param {string} key - The storage key
 * @returns {any|null} The stored value or null if not found/expired
 */
export function getItem(key) {
  if (typeof window === 'undefined' || !window.localStorage) {
    return null;
  }

  try {
    const itemStr = window.localStorage.getItem(key);
    if (!itemStr) {
      return null;
    }

    const item = JSON.parse(itemStr);

    // Check if item has expired
    if (item.ttl && Date.now() - item.timestamp > item.ttl) {
      window.localStorage.removeItem(key);
      return null;
    }

    return item.value;
  } catch (error) {
    console.error('Error getting localStorage item:', error);
    return null;
  }
}

/**
 * Remove item from localStorage
 *
 * @param {string} key - The storage key
 */
export function removeItem(key) {
  if (typeof window === 'undefined' || !window.localStorage) {
    return;
  }

  try {
    window.localStorage.removeItem(key);
  } catch (error) {
    console.error('Error removing localStorage item:', error);
  }
}

/**
 * Clear all items with a specific prefix
 *
 * @param {string} prefix - The key prefix to match
 */
export function clearByPrefix(prefix) {
  if (typeof window === 'undefined' || !window.localStorage) {
    return;
  }

  try {
    const keys = Object.keys(window.localStorage);
    keys.forEach(key => {
      if (key.startsWith(prefix)) {
        window.localStorage.removeItem(key);
      }
    });
  } catch (error) {
    console.error('Error clearing localStorage by prefix:', error);
  }
}

/**
 * Check if an item exists and is not expired
 *
 * @param {string} key - The storage key
 * @returns {boolean} True if item exists and is valid
 */
export function hasItem(key) {
  const value = getItem(key);
  return value !== null;
}

/**
 * Get expiry timestamp for an item
 *
 * @param {string} key - The storage key
 * @returns {number|null} The expiry timestamp or null
 */
export function getExpiry(key) {
  if (typeof window === 'undefined' || !window.localStorage) {
    return null;
  }

  try {
    const itemStr = window.localStorage.getItem(key);
    if (!itemStr) {
      return null;
    }

    const item = JSON.parse(itemStr);
    if (!item.ttl) {
      return null;
    }

    return item.timestamp + item.ttl;
  } catch (error) {
    console.error('Error getting expiry:', error);
    return null;
  }
}

/**
 * Check if an item is expired
 *
 * @param {string} key - The storage key
 * @returns {boolean} True if expired, false otherwise
 */
export function isExpired(key) {
  const expiry = getExpiry(key);
  if (!expiry) {
    return false;
  }

  return Date.now() > expiry;
}
